// `?url` faz o Vite copiar o worker para os assets e devolver o caminho local.
// Sem isto o pdf.js tenta baixar o worker de um CDN — o que quebraria offline,
// esbarraria em CSP e, pior, faria uma requisição externa numa tela que existe
// justamente para o PDF do paciente NÃO sair do navegador. É só uma string:
// não arrasta código nenhum para o bundle.
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { lerAgenda } from '@/recepcao/agendaParser';

/**
 * O pdf.js entra por `import()`, não no topo do arquivo.
 *
 * Estático, ele engordava o chunk principal em ~436 kB (1.012 → 1.448 kB) —
 * cobrados de TODA página, inclusive do inbox que a clínica deixa aberto o dia
 * inteiro, por uma tela que a recepção abre uma vez por dia. Dinâmico, o custo
 * só aparece quando alguém escolhe um PDF.
 *
 * O módulo fica em cache no navegador entre um arquivo e outro; guardar a
 * promessa evita reavaliar o wiring do worker a cada leitura.
 */
let pdfjsPromise = null;

function carregarPdfjs() {
  if (!pdfjsPromise) {
    pdfjsPromise = import('pdfjs-dist').then((pdfjs) => {
      pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
      return pdfjs;
    });
  }
  return pdfjsPromise;
}

/**
 * Lê o relatório de agenda do Careflow, inteiramente NO NAVEGADOR.
 *
 * O arquivo nunca sobe para servidor nenhum: é agenda de paciente, com nome,
 * telefone e observação clínica (PLANO seção 9 — LGPD). O `File` vira
 * ArrayBuffer, o pdf.js extrai os itens de texto com coordenadas, e o
 * `agendaParser` — que é puro e testável — faz o resto.
 *
 * @param arquivo  o File do <input type="file">
 * @returns `{ consultas, paginas }`
 */
export async function lerAgendaDoPdf(arquivo) {
  const pdfjs = await carregarPdfjs();
  const dados = new Uint8Array(await arquivo.arrayBuffer());
  // A tarefa é guardada: é NELA que vive o `destroy` (ver o finally abaixo).
  const tarefa = pdfjs.getDocument({ data: dados });
  const doc = await tarefa.promise;

  const paginas = [];
  try {
    for (let n = 1; n <= doc.numPages; n += 1) {
      const pagina = await doc.getPage(n);
      const conteudo = await pagina.getTextContent();
      paginas.push(
        conteudo.items
          // Itens de marcação não têm `transform`; só texto interessa.
          .filter((i) => Array.isArray(i.transform))
          .map((i) => ({
            str: i.str ?? '',
            x: i.transform[4],
            y: i.transform[5],
            width: i.width ?? 0,
          })),
      );
    }
  } finally {
    // Libera o worker mesmo se uma página falhar no meio — sem isto o
    // navegador segura a memória do PDF até a aba fechar.
    //
    // O `destroy` é da TAREFA, não do documento: em pdfjs-dist 6 o
    // `PDFDocumentProxy` não tem esse método (só `cleanup`, que não encerra o
    // worker). Chamá-lo em `doc` lançava TypeError — e, por estar no finally,
    // esse erro sobrepunha um parsing que já tinha dado certo, virando "não foi
    // possível ler" na tela.
    //
    // Tolerante de propósito: a limpeza é melhor-esforço e NUNCA deve derrubar
    // uma leitura bem-sucedida. Só que falhar calada esconderia um vazamento de
    // worker, então o aviso fica no console.
    try {
      if (typeof tarefa?.destroy === 'function') await tarefa.destroy();
    } catch (e) {
      console.warn('[OdontoSDR] destroy do PDF falhou (ignorado):', e);
    }
  }

  return { consultas: lerAgenda(paginas), paginas: paginas.length };
}
